import { Spinner, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
import { ProgressBar } from '@/components/widgets/a/ProgressBar/ProgressBar';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { isNullish } from '@/utils/booleans';

import { useOrganizationTokenUsage } from '../hooks/useOrganizationTokenUsage';

export const TokenUsageSummary = withSuspenseBoundary(
  () => {
    const { t } = useTranslation();
    const organizationId = useOrganizationId();

    const today = new Date().toISOString().split('T')[0];
    const tokenUsages = useOrganizationTokenUsage(organizationId, { current_date: today }).data;

    const ongoingTokenUsageConfiguration = tokenUsages.length > 0 ? tokenUsages.at(0) : null;

    if (isNullish(ongoingTokenUsageConfiguration)) {
      return (
        <span className='typography-caption text-text-secondary whitespace-pre-wrap'>
          {t('global.copilot.tokenUsage.noTokenUsageConfiguration.title')}
        </span>
      );
    }

    const usagePercentage = ongoingTokenUsageConfiguration?.usage_percentage;
    const validFrom = new Date(ongoingTokenUsageConfiguration.valid_from).toLocaleDateString();
    const validTo = new Date(ongoingTokenUsageConfiguration.valid_to).toLocaleDateString();

    return isNullish(usagePercentage) ? (
      <span className='typography-caption text-text-secondary whitespace-pre-wrap'>
        {t('global.copilot.tokenUsage.noTokenUsageConfiguration.title')}
      </span>
    ) : (
      <Stack className='gap-2 typography-caption text-text-secondary whitespace-pre-wrap'>
        <Stack className='w-full flex flex-row justify-between items-center'>
          <span>{t('global.copilot.tokenUsage.tokenUsageSummary.title')}</span>
          <span>{`${getDisplayNumber(usagePercentage, window.navigator.language)} %`}</span>
        </Stack>
        <ProgressBar value={usagePercentage ?? 0} max={100} />
        {t('global.copilot.tokenUsage.tokenUsageSummary.helper', {
          valid_from: validFrom,
          valid_to: validTo,
        })}
      </Stack>
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);
